import type {
  LeagueSummary,
  RoundEpisodePage,
  RoundPublic,
} from "@softmax/observatory-client/types";
import { parseDatetime } from "@softmax/web-features/time";
import type { RoundProgress, WatchLeagueState } from "./records";

export function roundProgress(
  page: Pick<RoundEpisodePage, "entries" | "next_cursor">,
): RoundProgress | null {
  // A partial page cannot honestly report the round's total progress.
  if (page.next_cursor !== null) return null;

  return {
    played: page.entries.filter((entry) => entry.status === "completed").length,
    total: page.entries.length,
  };
}

export function completedRounds(rounds: RoundPublic[]): RoundPublic[] {
  return rounds
    .filter((round) => round.status === "completed" && round.completed_at)
    .toSorted(
      (a, b) =>
        (parseDatetime(b.completed_at ?? null)?.getTime() ?? 0) -
        (parseDatetime(a.completed_at ?? null)?.getTime() ?? 0),
    );
}

export function activeRound(rounds: RoundPublic[]): RoundPublic | null {
  const active = rounds
    .filter((round) => round.status === "running" || round.status === "claimed")
    .toSorted(
      (a, b) =>
        (parseDatetime(b.created_at)?.getTime() ?? 0) -
        (parseDatetime(a.created_at)?.getTime() ?? 0),
    );
  return active[0] ?? null;
}

function roundIntervalMinutes(league: LeagueSummary): number | null {
  const candidates = [
    league.settings?.round_interval_minutes,
    league.commissioner_config?.schedule_interval_minutes,
  ];

  for (const value of candidates) {
    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
      return value;
    }
  }

  return null;
}

export type NextRoundOutlook =
  | {
      kind: "paused";
    }
  | {
      kind: "running";
      round: RoundPublic;
      dueAt: Date | null;
    }
  | {
      kind: "due";
      dueAt: Date;
    }
  | {
      kind: "unknown";
    };

export function nextRoundOutlook({
  league,
  state,
}: {
  league: LeagueSummary;
  state: Pick<WatchLeagueState, "activeRound" | "newestCompletedAt">;
}): NextRoundOutlook {
  if (league.rounds_paused_at) return { kind: "paused" };

  const interval = roundIntervalMinutes(league);
  const lastClose = parseDatetime(state.newestCompletedAt);
  const dueAt =
    interval != null && lastClose
      ? new Date(lastClose.getTime() + interval * 60000)
      : null;

  if (state.activeRound) {
    return { kind: "running", round: state.activeRound, dueAt };
  }

  if (!dueAt) return { kind: "unknown" };

  return { kind: "due", dueAt };
}
